<template>
  <div>
    <div class="panel-heading order-heading-with-stats">
      <h4 class="panel-title">{{ $t('orders.internationalShipmentOrdersTitle') }}</h4>

      <div class="order-heading-right">
        <div class="order-toolbar-buttons">
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="toggleFiltersVisible">
            <i class="material-icons">{{ showFilters ? 'expand_less' : 'expand_more' }}</i>
            <span v-if="showButtonLabels">{{ $t('orders.filtersToggleLabel') }}</span>
          </a>
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="reloadTable">
            <i class="material-icons">cached</i>
            <span v-if="showButtonLabels">{{ $t('datatable.reloadShortLabel') }}</span>
          </a>
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="sendToExport">
            <i class="material-icons">explicit</i>
            <span v-if="showButtonLabels">{{ $t('datatable.exportButton') }}</span>
          </a>
          <a v-if="$_has_perm('shipment_generate_qrcode')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openGenerateQrCodeModal()">
            <i class="fa fa-qrcode"></i>
            <span v-if="showButtonLabels">{{ $t('datatable.qrCodeShortLabel') }}</span>
          </a>
          <a v-if="$_has_perm('deliver_to_pending_to_branch')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openSendToPendingToBranchModal()">
            <i class="material-icons" style="color:#006b8d">send</i>
            <span v-if="showButtonLabels">{{ $t('datatable.sendToPendingToBranchButton') }}</span>
          </a>
          <a v-if="$_has_perm('advance_to_branch_delivery')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="sendAllToPendingToBranch()">
            <i class="material-icons" style="color:#006b8d">send</i>
            <span v-if="showButtonLabels">{{ $t('datatable.sendAllToBranchButton') }}</span>
          </a>
          <a v-if="$_has_perm('advance_to_branch_delivery_by_qrcode')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openSendToBranchDeliveryModal()">
            <i class="material-icons" style="color:#006b8d">send</i>
            <span v-if="showButtonLabels">{{ $t('datatable.sendToBranchDeliveryButton') }}</span>
          </a>
          <a class="order-toolbar-display-toggle" @click="$_toggle_toolbar_button_display()">
            <i class="material-icons">{{ showButtonLabels ? 'label_off' : 'label' }}</i>
          </a>
        </div>

        <div class="order-stats-row order-stats-row--compact">
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--blue"><i class="material-icons">list_alt</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('reports.totalOrders') }}</span>
              <span class="order-stat-value">{{ summaryTotalOrders }}</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--green"><i class="material-icons">attach_money</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('printLogs.totalAmountLabel') }}</span>
              <span class="order-stat-value">$ {{ summaryTotalAmount.toFixed(2) }}</span>
              <span v-if="qrCodeValues.length > 0" class="order-stat-note">({{ qrCodeValues.length }} selected)</span>
              <span v-else-if="!filterMade" class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--purple"><i class="material-icons">today</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('adminDashboard.ordersTodayLabel') }}</span>
              <span class="order-stat-value">{{ ordersTodayCount }}</span>
              <span class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--orange"><i class="material-icons">payment</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('adminDashboard.pendingPaymentsLabel') }}</span>
              <span class="order-stat-value">{{ pendingPaymentsCount }}</span>
              <span class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="panel-body"
      style="display: block;"
    >
      <div class="row">
        <preDatatableComponent
          ref="preDatatable"
          :permissions="{
            deliver_to_pending_to_branch: 'deliver_to_pending_to_branch',
            deliver_to_branch: 'advance_to_branch_delivery',
            deliver_to_branch_by_qrcode: 'advance_to_branch_delivery_by_qrcode',
            generate_qr_code: 'shipment_generate_qrcode'
          }"
          :removeAddButton="true"
          :removeWarehouseDeliverButton="true"
          :hideReloadButton="true"
          :collapsibleFilters="true"
          :filtersVisible="showFilters"
          :openSendToPendingToBranchModal="openSendToPendingToBranchModal"
          :sendAllToPendingToBranch="sendAllToPendingToBranch"
          :openSendToBranchDeliveryModal="openSendToBranchDeliveryModal"
          :tableData="tableData"
          :sendToExport="sendToExport"
          :resetGetResults="resetGetResults"
          :resetGetResults2="resetGetResults2"
          :searchMe="searchMe"
          :enableCustomerSearchSuggestions="true"
          :pagination="pagination"
          :perPageFix="perPageFix"
          :filterTable="filterTable"
          :getByWarehouse="getByWarehouse"
          :openGenerateQrCodeModal="openGenerateQrCodeModal"
          :columns="columns"
          :showHideColumn="showHideColumn"
          :defaultHideCol="defaultHideCol"
          :storageKey="'cols_intShipmentOrder_v3'"
          :resultsVersion="resultsVersion"
          :warehouseFilterItems="warehouses"
          :warehouseFilterChange="handleWarehouseFilter"
          :filterMade="filterMade"
          :totalResult="totalResult"
          @branch-edit="branchEdit"
        ></preDatatableComponent>
        <!-- :intShipment="true" -->
        <div class="col-xs-12">
          <div class="row text-center">
            <span
              id="test-editable"
              style="opacity:0"
            ></span>
          </div>

          <div class="table-responsive oc-table-shell">
          <datatable
            v-if="!isMobile"
            :sortDir="tableData.sortDir"
            :columns="columns"
            :sortByColumn="sortByColumn"
            :searchOneCol="searchOneCol"
            :sortKey="sortKey"
            :sortOrders="sortOrders"
            :checkAll="checkAll"
            :unCheckAll="unCheckAll"
          >
            <tbody>
              <tr
                v-for="(r, index) in apiResults"
                :key="r.order_id"
                :style="qrCodeValues.find(x => x[0] == r.order_id) ? 'background-color: #FFF9C4' : '' "
              >
                <td>
                  <input
                    :id="'cb' + index"
                    type="checkbox"
                    :value="[
                      r.order_id,
                      r.full_name,
                      r.phone_number,
                      r.user_id,
                      r.theOrderID,
                      r.is_ship_only,
                      r.product_name,
                    ]"
                    v-model="qrCodeValues"
                  />
                </td>
                <td class="oc-cell hover-container">
                  <div class="oc-order-number">
                    <i v-if="r.qrcode" class="fa fa-qrcode" aria-hidden="true"></i> #{{ r.theOrderID }}
                  </div>
                  <div class="oc-order-sub" v-if="r.qrcode"><i class="fa fa-qrcode" aria-hidden="true"></i> {{ r.qrcode }}</div>
                  <div class="oc-order-sub" v-if="r.track_number">{{ $t('columns.trackNumber') }}: {{ r.track_number }}</div>
                  <div class="oc-order-sub" v-if="r.last_order_invoice">{{ $t('columns.invoiceNumber') }}: {{ r.last_order_invoice }}</div>
                  <div class="oc-order-sub"><view-order-users :orderData="r" /></div>
                </td>
                <td class="oc-cell ws-nowrap">
                  <img v-if="r.profile_image" :src="r.profile_image && r.profile_image.startsWith('http') ? r.profile_image : `${$_image_path()}${r.profile_image}`" class="customer-profile-image" />
                  <customer-device-icon :orderSource="r.order_source" />
                  <span class="customer-name-stack">
                    <span v-if="r.full_name && r.full_name.trim().split(/\s+/).length > 3" style="word-break: break-word; white-space: pre-line;">
                      {{ r.full_name.replace(/((?:\S+\s+){3})/g, '$1\n') }}
                    </span>
                    <span v-else>{{ r.full_name }}</span>
                    <span v-if="r.phone_number" class="customer-phone-line">{{ r.phone_number }}</span>
                    <span v-if="r.phone_number_2" class="customer-phone-line">{{ r.phone_number_2 }}</span>
                  </span>
                  <open-whatsapp-and-viber :phoneNumber="r.phone_number" />
                </td>
                <td class="oc-cell">
                  <div class="oc-item-name">{{ r.product_name ? r.product_name.substr(0, 35) + (r.product_name.length > 35 ? '...' : '') : '—' }}</div>
                  <div class="oc-item-sub" v-if="r.brand">{{ $t('columns.brand') }}: <strong>{{ r.brand }}</strong></div>
                  <a v-if="r.product_url" :href="r.product_url" target="_blank" rel="noopener noreferrer" class="oc-item-link">
                    <i class="material-icons">link</i> {{ $t('columns.url') }}
                  </a>
                </td>
                <td class="oc-cell oc-product-cell">
                  <img v-if="r.image" :src="r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`" class="oc-thumb" @click="lightboxImage = r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`; lightboxOpen = true" />
                  <span v-else class="oc-thumb-placeholder">—</span>
                </td>
                <td class="oc-cell">
                  <span class="oc-status-chip" style="background:#A655BE; color:#FFFFFF;">{{ $_status_label(r.status) }}</span>
                  <span
                    v-if="r.is_ship_only == 1"
                    class="oc-status-chip rtl-ml-2"
                    style="background-color:#FCE4EC; color:#C2185B;"
                  >{{ $t('columns.shipOnly') }}</span>
                  <div class="oc-location-line" v-if="r.country">
                    <i class="material-icons oc-location-icon">place</i>{{ r.country }}<span v-if="r.transportation_type_obj"> · {{ r.transportation_type_obj.name.replace('Transportation', '') }}</span>
                  </div>
                  <div class="oc-item-sub" v-if="r.purchase_note">{{ $t('columns.purchaseNote') }}: {{ r.purchase_note }}</div>
                </td>
                <td class="combined-cell-td oc-cell">
                  <div class="combined-cell">
                    <div><span class="combined-cell-label">{{ $t('columns.price') }}:</span> {{ r.item_price ? '$' + r.item_price : '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.shipping') }}:</span> {{ r.internal_shipping_handling ? '$' + r.internal_shipping_handling : '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.total') }}:</span> {{ r.total_amount ? '$' + r.total_amount : '—' }}</div>
                    <div class="combined-cell-divider"></div>
                    <div><span class="combined-cell-label">{{ $t('columns.qty') }}:</span> {{ r.quantity || '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.size') }}:</span> {{ r.size || '—' }}</div>
                  </div>
                </td>
                <td class="oc-cell hover-container" style="min-width: 150px">
                  <view-order-dates :orderData="r" stack-time />
                  <span
                    v-if="r.payment_status"
                    class="oc-payment-chip"
                    :style="{ background: paymentStatusColor(r.payment_status, r.invoice_edit_count).bg, color: paymentStatusColor(r.payment_status, r.invoice_edit_count).color }"
                  >{{ paymentStatusLabel(r.payment_status) }}</span>
                </td>
                <td class="oc-cell oc-actions-cell">
                  <div class="oc-actions-grid">
                    <a
                      v-if="$_has_perm('view_order_detail')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.viewDetails')"
                      @click="initDetail('modal_showApp', r.order_id)"
                    >
                      <i class="material-icons" style="color:#01579B">aspect_ratio</i>
                    </a>
                    <a
                      v-if="$_has_perm('deliver_to_pending_to_branch')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.deliverToPendingToBranchTooltip')"
                      @click="deliverToPendingToBranch(r.order_id)"
                    >
                      <i class="material-icons" style="color:#EE4545">local_shipping</i>
                    </a>
                    <a
                      v-if="$_has_perm('advance_to_branch_delivery')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.sendToBranchDeliveryTooltip')"
                      @click="advanceOrderToBranchDelivery(r.order_id)"
                    >
                      <i class="material-icons" style="color:#8E24AA">local_shipping</i>
                    </a>
                    <a
                      v-if="$_has_perm('return_shipment_order_to_purchased_order')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.returnToPurchaseTooltip')"
                      @click="returnToPurchase(r.order_id)"
                    >
                      <i class="material-icons" style="color:#eb8545">settings_backup_restore</i>
                    </a>
                    <a
                      v-if="$_has_perm('cancel_int_shipment_order')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.cancelThisOrder')"
                      @click="openCancelingOrderModal(r.order_id, r.total_amount)"
                    >
                      <i class="material-icons" style="color:#c62828">cancel</i>
                    </a>
                    <a
                      v-if="$_has_perm('process_order')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.processOrderTooltip')"
                      @click="openProcessOrderModal(r)"
                    >
                      <i class="material-icons" style="color:#424242">settings</i>
                    </a>
                  </div>
                </td>
              </tr>
              <tr id="no-result-row">
                <td
                  :colspan="columns.length"
                  class="text-center"
                >
                  <v-alert
                    type="error"
                    :value="alertValue"
                  >
                    {{ $t('common.noResultsFound') }}
                  </v-alert>
                </td>
              </tr>
            </tbody>
          </datatable>
          </div>

          <div v-if="isMobile">
            <div
              v-for="(r, index) in apiResults"
              :key="r.order_id"
              class="moc-container"
            >
              <div class="moc-card" :style="qrCodeValues.find(x => x[0] == r.order_id) ? 'border-left: 3px solid #FBC02D;' : ''">
                <div class="moc-header">
                  <input
                    :id="'cb' + index"
                    type="checkbox"
                    :value="[r.order_id, r.full_name, r.phone_number, r.user_id, r.theOrderID, r.is_ship_only, r.product_name]"
                    v-model="qrCodeValues"
                    class="rtl-mr-8"
                  />
                  <span class="moc-item-code">
                    <i v-if="r.qrcode" class="fa fa-qrcode" aria-hidden="true"></i> {{ r.theOrderID }}
                    <span v-if="r.track_number" style="font-size:0.8rem; color:#555" class="rtl-ml-6">| {{ r.track_number }}</span>
                  </span>
                  <span class="moc-status-badge" style="background:#A655BE; color:#FFFFFF;">{{ $_status_label(r.status) }}</span>
                </div>
                <div class="moc-body">
                  <!-- QR -->
                  <div class="moc-row" v-if="r.qrcode">
                    <span class="moc-label">{{ $t('columns.qr') }}</span>
                    <span class="moc-value"><i class="fa fa-qrcode" aria-hidden="true"></i> {{ r.qrcode }}</span>
                  </div>
                  <!-- Invoice # | Customer ID -->
                  <div class="moc-row--2col">
                    <div><span class="moc-label">{{ $t('recordInvoicePayment.invoiceHashLabel') }}</span><span class="moc-value">{{ r.last_order_invoice || '\u2014' }}</span></div>
                    <div><span class="moc-label">{{ $t('columns.customerId') }}</span><span class="moc-value">{{ r.customer_id_fk || '\u2014' }}</span></div>
                  </div>
                  <!-- Phone Number -->
                  <div class="moc-row">
                    <span class="moc-label">{{ $t('columns.phone') }}</span>
                    <span class="moc-value">{{ r.phone_number || '\u2014' }}</span>
                  </div>
                  <!-- User -->
                  <div class="moc-row">
                    <span class="moc-label">{{ $t('common.user') }}</span>
                    <span class="moc-value"><view-order-users :orderData="r" /></span>
                  </div>
                  <!-- Customer -->
                  <div class="moc-row">
                    <span class="moc-label">{{ $t('columns.customer') }}</span>
                    <span class="moc-value">
                      <img v-if="r.profile_image" :src="r.profile_image && r.profile_image.startsWith('http') ? r.profile_image : `${$_image_path()}${r.profile_image}`" class="customer-profile-image" />
                      <customer-device-icon :orderSource="r.order_source" />
                      <span v-if="r.full_name && r.full_name.trim().split(/\s+/).length > 3" style="word-break:break-word; white-space:pre-line;">{{ r.full_name.replace(/((?:\S+\s+){3})/g, '$1\n') }}</span>
                      <span v-else>{{ r.full_name }}</span>
                      <open-whatsapp-and-viber :phoneNumber="r.phone_number" />
                    </span>
                  </div>
                  <!-- Item Name -->
                  <div class="moc-row">
                    <span class="moc-label">{{ $t('columns.itemName') }}</span>
                    <span class="moc-value">{{ r.product_name ? r.product_name.substr(0, 60) + (r.product_name.length > 60 ? '...' : '') : '\u2014' }}</span>
                  </div>
                  <!-- Brand -->
                  <div class="moc-row" v-if="r.brand">
                    <span class="moc-label">{{ $t('columns.brand') }}</span>
                    <span class="moc-value">{{ r.brand }}</span>
                  </div>
                  <!-- URL -->
                  <div class="moc-row">
                    <span class="moc-label">{{ $t('columns.url') }}</span>
                    <span class="moc-value">
                      <a v-if="r.product_url" :href="r.product_url" target="_blank" rel="noopener noreferrer" style="color:#01579B;">
                        <i class="material-icons" style="font-size:20px; vertical-align:middle;">link</i>
                      </a>
                      <span v-else style="color:#bbb;">&#x2014;</span>
                    </span>
                  </div>
                  <!-- Image -->
                  <div class="moc-row" v-if="r.image">
                    <span class="moc-label">{{ $t('columns.image') }}</span>
                    <span class="moc-value">
                      <img
                        :src="r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`"
                        style="width:80px; height:80px; object-fit:cover; border-radius:4px; cursor:pointer;"
                        @click="lightboxImage = r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`; lightboxOpen = true"
                      />
                    </span>
                  </div>
                  <!-- Date & Time -->
                  <div class="moc-row">
                    <span class="moc-label">{{ $t('columns.dateTime') }}</span>
                    <span class="moc-value"><view-order-dates :orderData="r" /></span>
                  </div>
                  <!-- Warehouse | Type -->
                  <div class="moc-row--2col">
                    <div>
                      <span class="moc-label">{{ $t('columns.warehouse') }}</span>
                      <span class="moc-value">{{ r.country || '\u2014' }}</span>
                    </div>
                    <div>
                      <span class="moc-label">{{ $t('columns.type') }}</span>
                      <span class="moc-value">{{ r.transportation_type_obj ? r.transportation_type_obj.name.replace('Transportation', '') : '\u2014' }}</span>
                    </div>
                  </div>
                  <!-- Price | Shipping | Total -->
                  <div class="moc-row--3col">
                    <div>
                      <span class="moc-label">{{ $t('columns.price') }}</span>
                      <span class="moc-value">{{ r.item_price ? '$' + r.item_price : '\u2014' }}</span>
                    </div>
                    <div>
                      <span class="moc-label">{{ $t('columns.shipping') }}</span>
                      <span class="moc-value">{{ r.internal_shipping_handling ? '$' + r.internal_shipping_handling : '\u2014' }}</span>
                    </div>
                    <div>
                      <span class="moc-label">{{ $t('columns.total') }}</span>
                      <span class="moc-value">{{ r.total_amount ? '$' + r.total_amount : '\u2014' }}</span>
                    </div>
                  </div>
                  <!-- Size -->
                  <div class="moc-row" v-if="r.size">
                    <span class="moc-label">{{ $t('columns.size') }}</span>
                    <span class="moc-value">{{ r.size }}</span>
                  </div>
                  <!-- Shiponly -->
                  <div class="moc-row moc-row--2col">
                    <span class="moc-label">{{ $t('columns.shipOnly') }}</span>
                    <span class="moc-value">{{ r.is_ship_only == 1 ? $t('common.yes') : $t('common.no') }}</span>
                  </div>
                  <!-- Purchase Note -->
                  <div v-if="r.purchase_note" class="moc-row">
                    <span class="moc-label">{{ $t('columns.purchaseNote') }}</span>
                    <span class="moc-value">{{ r.purchase_note }}</span>
                  </div>
                </div>
                <div class="moc-actions">
                  <a
                    v-if="$_has_perm('view_order_detail')"
                    style="color:#01579B"
                    :tooltip="$t('orders.viewDetails')"
                    @click="initDetail('modal_showApp', r.order_id)"
                  >
                    <i class="material-icons">aspect_ratio</i>
                  </a>
                  <a
                    v-if="$_has_perm('deliver_to_pending_to_branch')"
                    style="color:#EE4545"
                    :tooltip="$t('orders.deliverToPendingToBranchTooltip')"
                    @click="deliverToPendingToBranch(r.order_id)"
                  >
                    <i class="material-icons">local_shipping</i>
                  </a>
                  <a
                    v-if="$_has_perm('advance_to_branch_delivery')"
                    style="color:#8E24AA"
                    :tooltip="$t('orders.sendToBranchDeliveryTooltip')"
                    @click="advanceOrderToBranchDelivery(r.order_id)"
                  >
                    <i class="material-icons">local_shipping</i>
                  </a>
                  <a
                    v-if="$_has_perm('return_shipment_order_to_purchased_order')"
                    style="color:#eb8545"
                    :tooltip="$t('orders.returnToPurchaseTooltip')"
                    @click="returnToPurchase(r.order_id)"
                  >
                    <i class="material-icons">settings_backup_restore</i>
                  </a>
                  <a
                    v-if="$_has_perm('cancel_int_shipment_order')"
                    style="color:#c62828"
                    :tooltip="$t('orders.cancelThisOrder')"
                    @click="openCancelingOrderModal(r.order_id, r.total_amount)"
                  >
                    <i class="material-icons">cancel</i>
                  </a>
                  <a
                    v-if="$_has_perm('process_order')"
                    style="color:#424242"
                    :tooltip="$t('orders.processOrderTooltip')"
                    @click="openProcessOrderModal(r)"
                  >
                    <i class="material-icons">settings</i>
                  </a>
                </div>
              </div>
            </div>
            <v-alert type="error" :value="alertValue">{{ $t('common.noResultsFound') }}</v-alert>
          </div>

          <v-dialog v-model="lightboxOpen" max-width="600">
            <v-card v-if="lightboxImage">
              <v-card-text style="padding:0; text-align:center; background:#000;">
                <img :src="lightboxImage" style="max-width:100%; max-height:80vh; object-fit:contain;" />
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="lightboxOpen = false">{{ $t('common.close') }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <view-order-details
            :detialData="detialData"
            :closeModal="closeModal"
          ></view-order-details>

          <send-to-pending-to-branch
            :sendToPendingToBranchData="sendToPendingToBranchData"
            :sendToPendingToBranch="sendToPendingToBranch"
            :sendToPendingToBranchModal="sendToPendingToBranchModal"
            :openSendToPendingToBranchModal="openSendToPendingToBranchModal"
            :closeSendToPendingToBranchModal="closeSendToPendingToBranchModal"
          >
          </send-to-pending-to-branch>

          <send-to-branch-delivery
            :sendToBranchDeliveryData="sendToBranchDeliveryData"
            :sendToBranchDelivery="sendToBranchDelivery"
            :sendToBranchDeliveryModal="sendToBranchDeliveryModal"
            :openSendToBranchDeliveryModal="openSendToBranchDeliveryModal"
            :closeSendToBranchDeliveryModal="closeSendToBranchDeliveryModal"
          >
          </send-to-branch-delivery>

          <create-qr-code
            :openGenerateQrCodeModal="openGenerateQrCodeModal"
            :generateQrCodeModal="generateQrCodeModal"
            :closeGenerateQrCodeModal="closeGenerateQrCodeModal"
            :qrCodeValues="qrCodeValues"
            :generateQrCode="generateQrCode"
            :getResults="getResults"
          ></create-qr-code>

          <process-the-order
            v-if="currentProcessOrder"
            ref="processComp"
            :processOrder="processOrder"
            :r="currentProcessOrder"
            :checkAlert="checkAlert"
            :updateData="updateData"
            :calculateTotalAmount="calculateTotalAmount"
            :loading="loading"
            :getResults="getResults"
            :clearProcessOrderData="clearProcessOrderData"
            :theQuantity="theQuantity"
            :itemTypes="itemTypes"
            :warehouses="warehouses"
            :reasons="reasons"
          ></process-the-order>

          <admin-cancel-order
            :closeCancelingOrderModal="closeCancelingOrderModal"
            :cancelOrderModal="cancelOrderModal"
            :cancelAlert="cancelAlert"
            :cancelData="cancelData"
            :cancelingOrder="cancelingOrder"
            :reasons="reasons"
            :willAutoRefund="true"
            :refundAmount="cancelRefundAmount"
          ></admin-cancel-order>

          <div class="row">
            <div class="col-xs-12 text-center oc-pagination">
              <paginate
                :page-count="pagination.lastPage"
                :page-range="2"
                :changePage="changePage"
                :container-class="'pagination'"
                :pagination="pagination"
                :page-class="'page-item'"
              >
              </paginate>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationVue from './Pagination.vue'
import preDatatableComponent from './preDatatableComponent.vue'
import DatatableVue from './Datatable.vue'
import viewOrderDetails from './viewOrderDetails.vue'
import sendToPendingToBranch from './sendToPendingToBranch.vue'
import sendToBranchDelivery from './sendToBranchDelivery.vue'
import createQrCode from './createQrCode.vue'
import processTheOrder from './processTheOrder.vue'
import adminCancelOrder from './adminCancelOrder'
import axios from 'axios'
import swal from 'sweetalert'
import statusHelpers from '../mixins/statusHelpers'
import toolbarButtonDisplay from '../mixins/toolbarButtonDisplay'

export default {
  mixins: [statusHelpers, toolbarButtonDisplay],
  components: {
    preDatatableComponent: preDatatableComponent,
    datatable: DatatableVue,
    paginate: PaginationVue,
    'view-order-details': viewOrderDetails,
    'send-to-pending-to-branch': sendToPendingToBranch,
    'send-to-branch-delivery': sendToBranchDelivery,
    'create-qr-code': createQrCode,
    'process-the-order': processTheOrder,
    'admin-cancel-order': adminCancelOrder
  },
  created() {
    this.getLoggedInUser()
  },
  mounted() {
    this.$_fetch_statuses()
    this.perPageFix()
    this.getWarehouses()
    this.getItemType()
    this.getReasons()

    if (screen.width <= 600) {
      this.isMobile = true
    } else {
      this.isMobile = false
    }
  },
  $_veeValidate: {
    validator: 'new',
  },
  data() {
    let sortOrders = {}
    // Grouped columns (redesign, mirrors allOrder.vue's pattern) -- was a flat ~24-column
    // list; each grouped cell stacks several related fields (QR / track # / invoice # /
    // shipped-by user fold into Order Info; ship-only & purchase note into Status & Location).
    // Per-column hide now only makes sense at the group level, so defaultHideCol is emptied
    // and storageKey bumped so stale hidden-column selections don't misapply to new positions.
    let columns = [
      { label: '', name: '', text: this.$t('columns.checkBoxColumn'), value: '1' },
      { label: this.$t('columns.orderInfo'), text: this.$t('columns.orderInfo'), value: '2', sortable: true, searchable: true, name: 'theOrderID' },
      { label: this.$t('columns.customer'), text: this.$t('columns.customer'), value: '3', sortable: true, searchable: true, name: 'full_name' },
      { label: this.$t('columns.itemDetails'), text: this.$t('columns.itemDetails'), value: '4', sortable: true, searchable: true, name: 'product_name' },
      { label: this.$t('columns.product'), text: this.$t('columns.product'), value: '5', sortable: false, searchable: false, name: 'image' },
      { label: this.$t('columns.statusLocation'), text: this.$t('columns.statusLocation'), value: '6', sortable: true, searchable: true, name: 'status' },
      { label: this.$t('columns.pricing'), text: this.$t('columns.pricing'), value: '7', sortable: false, searchable: false, name: 'price_all' },
      { label: this.$t('columns.datePayment'), text: this.$t('columns.datePayment'), value: '8', sortable: true, searchable: false, name: 'updated_at' },
      { label: this.$t('columns.actions'), text: this.$t('columns.actions'), value: '9', sortable: false, searchable: false, name: 'actions' }
    ]
    columns.forEach((column) => {
      sortOrders[column.name] = -1
    })

    return {
      isMobile: false,
      loader: null,
      loading: false,
      url: 'api/int_shipment_order',
      deliverToPendingToBranchUrl: 'api/deliver_to_pending_to_branch',
      advanceToBranchDeliveryUrl: 'api/advance_to_branch_delivery',
      advanceToBranchDeliveryByQrcodeUrl: 'api/advance_to_branch_delivery_by_qrcode',
      orderDetailUrl: 'api/show_order_detail',
      returnToPurchaseUrl: 'api/orders/',
      processOrderUrl: 'api/orders',
      getItemTypeUrl: 'api/get-item-type',
      cancelingOrderUrl: 'api/canceling_order_by_admin',
      sendToPendingToBranchModal: false,
      sendToBranchDeliveryModal: false,
      alertValue: false,
      filterMade: false,
      totalResult: 0,
      resultsVersion: 0,
      generateQrCodeModal: false,
      checkAlert: false,
      btnsave: true,
      queryParameter: true,
      qrCodeValues: [],
      loggedInUserData: {},
      reasons: [],
      cancelOrderModal: false,
      cancelAlert: false,
      theCancelOrderID: '',
      cancelRefundAmount: null,
      cancelData: {
        cancel_reason: ''
      },
      sendToPendingToBranchData: {
        order_id: '',
        send_sms: false,
      },
      sendToBranchDeliveryData: {
        order_id: '',
        send_sms: false,
      },
      // Process Order (single instance pattern, mirrors allOrder.vue)
      currentProcessOrder: null,
      theQuantity: { quantity: '' },
      itemTypeResult: [],
      itemTypes: [],
      updateData: {
        item_price: null,
        the_tl_price: null,
        the_aed_price: null,
        the_cny_price: null,
        the_gbp_price: null,
        the_eur_price: null,
        tax: null,
        national_tax: null,
        item_weight: null,
        internal_shipping_handling: null,
        delivery_price: null,
        kreenex_fee: null,
        quantity_charge: null,
        insurance: null,
        total_amount: null,
        from_date: null,
        to_date: null,
        description: '',
        tmp_shipping: null,
        item_weight_lb: null,
        price_weight: null,
        send_sms: false,
        discount: null,
        item_type: '',
        transportation_type: null,
        is_repackage: false,
        is_hold: false,
        other_service: '',
        local_delivery: 0,
        process_note: '',
        customer_process_note: '',
        repackage_price: null,
        hold_price: null,
        other_service_price: null,
        customer_id_fk: null,
        product_name: null,
        product_url: null,
        quantity: null,
        color: null,
        size: null,
        note: null,
        brand: null
      },
      detialData: {},
      prevSearchQuery: '',
      warehouses: [],
      lightboxImage: null,
      lightboxOpen: false,
      apiResults: [],
      orderID: [],
      detailResults: [],
      // Grouped cells can't be hidden field-by-field any more -- same tradeoff allOrder.vue made.
      defaultHideCol: [],
      // Hidden by default (per-page preference, same localStorage convention as allOrder.vue).
      showFilters: localStorage.getItem('filtersVisible_intShipmentOrder') === 'true',
      columns: columns,
      sortKey: 'updated_at',
      sortOrders: sortOrders,
      tableData: {
        per_page: 10,
        search: '',
        column: 'updated_at',
        sortDir: 'desc',
        searchOneCol: '',
        searchOneVal: '',
        from_date: '',
        to_date: '',
        warehouse: '',
        warehouse_id_fk: '',
        branches_id: ''
      },
      pagination: {
        lastPage: 1,
        currentPage: 1,
        total: 1,
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
        from: '',
        to: '',
      },
    }
  },

  computed: {
    summaryTotalOrders() {
      if (this.qrCodeValues.length > 0) return this.qrCodeValues.length
      return this.filterMade
        ? this.apiResults.length
        : (this.pagination.total || this.apiResults.length)
    },
    summaryTotalAmount() {
      return this.apiResults.reduce((sum, r) => sum + (Number(r.total_amount) || 0), 0)
    },
    // Best-effort only: counts within the currently loaded page of apiResults, not a true
    // system-wide count -- hence the "(current page)" note shown alongside it in the stat card.
    ordersTodayCount() {
      const today = new Date().toISOString().split('T')[0]
      return this.apiResults.filter(r => {
        const d = r.int_shipment_date || r.updated_at || r.order_date || r.created_at
        return d && d.substr(0, 10) === today
      }).length
    },
    // Same current-page-only caveat as ordersTodayCount above.
    pendingPaymentsCount() {
      return this.apiResults.filter(r => r.payment_status && r.payment_status !== 'paid').length
    }
  },

  methods: {
    toggleFiltersVisible() {
      this.showFilters = !this.showFilters
      localStorage.setItem('filtersVisible_intShipmentOrder', this.showFilters ? 'true' : 'false')
    },

    // Replicates preDatatableComponent.vue's own built-in Reload button (now relocated
    // into the panel-heading here) -- reaches into the child's own filter-chip/date-range
    // reset state via $refs since that state lives inside the shared component, not here.
    reloadTable() {
      this.resetGetResults()
      const preDatatable = this.$refs.preDatatable
      if (preDatatable) {
        preDatatable.filterApplied = false
        preDatatable.resetDatePickerDefaults()
        preDatatable.ware = 'Select'
      }
    },

    paymentStatusColor(status, editCount) {
      if (status === 'not_paid' && !Number(editCount)) {
        return { bg: '#E0E0E0', color: '#616161' }
      }
      const map = {
        not_paid: { bg: '#F63049', color: '#FFFFFF' },
        partial_paid: { bg: '#FFC85C', color: '#6B4C00' },
        paid: { bg: '#89D7B7', color: '#1A5940' },
        cancel: { bg: '#FB8C00', color: '#FFFFFF' }
      }
      return map[status] || { bg: '#F5F5F5', color: '#616161' }
    },

    paymentStatusLabel(status) {
      if (!status) return ''
      return status.replace(/_/g, ' ')
    },

    branchEdit(branches) {
      this.tableData.branches_id = branches.toString()
      this.getResults()
    },

    showHideColumn(prevColVal, currentColVal) {
      let a = []
      let diff = []

      if (currentColVal.length < prevColVal.length) {
        for (let i = 0; i < prevColVal.length; i++) {
          a[prevColVal[i]] = true
        }

        for (let i = 0; i < currentColVal.length; i++) {
          if (a[currentColVal[i]]) {
            delete a[currentColVal[i]]
          } else {
            a[currentColVal[i]] = true
          }
        }

        for (let k in a) {
          diff.push(k)
        }

        for (let c = 0; c < diff.length; c++) {
          $('td:nth-child(' + diff[c] + '),th:nth-child(' + diff[c] + ')').toggle()
        }
      } else {
        for (let i = 0; i < currentColVal.length; i++) {
          let visible = $(
            'td:nth-child(' + currentColVal[i] + '),th:nth-child(' + currentColVal[i] + ')'
          ).is(':visible')
          if (visible) {
            $(
              'td:nth-child(' + currentColVal[i] + '),th:nth-child(' + currentColVal[i] + ')'
            ).toggle()
          }
        }
      }
    },

    openGenerateQrCodeModal() {
      if (this.qrCodeValues.length < 1) {
        swal({
          text: this.$t('orders.pleaseAtLeastSelectOneOrder'),
          icon: 'warning',
          dangerMode: true,
        })
      } else {
        this.generateQrCodeModal = true
      }
    },

    closeGenerateQrCodeModal() {
      this.generateQrCodeModal = false
      this.qrCodeValues = []
    },

    generateQrCode() {
      this.closeGenerateQrCodeModal()
    },

    sendToExport() {
      let arr = ''

      for (let i = 0; i < this.orderID.length; i++) {
        if (i < this.orderID.length - 1) {
          arr = arr.concat('arr[]=' + this.orderID[i] + '&')
        } else {
          arr = arr.concat('arr[]=' + this.orderID[i])
        }
      }

      let hostName = window.location.hostname
      let protocol = window.location.protocol + '//'
      let fullHostName = protocol + hostName
      window.location.replace(fullHostName + '/api/shipping-export?' + arr)
    },

    getOrderID(orders) {
      this.orderID = []
      for (let i = 0; i < orders.length; i++) {
        this.orderID[i] = orders[i].order_id
      }
    },

    getLoggedInUser() {
      this.loggedInUserData = JSON.parse(localStorage.getItem('user_data'))
    },

    getResults(url = this.url) {
      var token = localStorage.getItem('access_token')
      let theDatatable = document.querySelector('.the-datatable')
      let thePanel = document.querySelector('.panel-flat')

      // A selection made before a filter/search/reload must not silently carry over into
      // the new result set — otherwise a stale row (no longer even visible) stays selected
      // and gets included in the next bulk action, affecting rows the user never intended
      // to touch in the current (filtered) view.
      this.qrCodeValues = []

      if (theDatatable != null) {
        theDatatable.classList.add('slide-table-out')
        thePanel.style.overflowX = 'auto'
      }

      axios
        .get(url, { params: this.tableData, headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let data = response.data
          const isFlatArray = Array.isArray(data.data)
          if (isFlatArray) {
            this.filterMade = true
            this.apiResults = data.data
          } else {
            this.filterMade = false
            this.apiResults = data.data.data
          }
          this.resultsVersion++

          if (this.apiResults.length === 0) {
            this.tableData.searchOneCol = ''
            this.tableData.searchOneVal = ''
            this.tableData.search = ''
            this.alertValue = true
          } else {
            this.alertValue = false
          }
          if (isFlatArray) {
            this.removeConfigPagination()
          } else {
            this.configPagination(data.data)
          }
          this.getOrderID(this.apiResults)
          if (theDatatable != null) {
            theDatatable.classList.remove('slide-table-out')
            setTimeout(function () {
              thePanel.style.overflowX = 'auto'
            }, 0)
          }
        })
        .then(() => {
          this.totalResult = 0
          for (let i = 0; i < this.apiResults.length; i++) {
            this.totalResult += 1
          }
        })
        .catch(() => { })
    },

    removeConfigPagination() {
      this.pagination.lastPage = ''
      this.pagination.currentPage = ''
      this.pagination.total = ''
      this.pagination.lastPageUrl = ''
      this.pagination.nextPageUrl = ''
      this.pagination.prevPageUrl = ''
      this.pagination.from = ''
      this.pagination.to = ''
    },

    resetGetResults() {
      this.queryParameter = false
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      this.tableData.search = ''
      this.sortKey = 'updated_at'
      this.tableData.sortDir = 'desc'
      this.tableData.column = 'updated_at'
      this.tableData.from_date = ''
      this.tableData.to_date = ''
      this.tableData.warehouse = ''
      this.tableData.warehouse_id_fk = ''
      this.getResults()
    },

    resetGetResults2() {
      this.resetGetResults()
    },

    getWarehouses() {
      this.warehouses = []
      let token = localStorage.getItem('access_token')
      axios
        .get('api/warehouses', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          response.data.data.forEach((r) => {
            this.warehouses.push({ text: r.country, value: r.warehouse_id, transportation_types: r.transportation_types, currency: r.currency, estimate_days: r.estimate_days })
          })
        })
        .catch(() => {})
    },

    getItemType() {
      let token = localStorage.getItem('access_token')
      axios
        .get(this.getItemTypeUrl, { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          this.itemTypeResult = response.data.data
          this.itemTypeResult.forEach((c) => {
            this.itemTypes.push({
              text: c.type_name,
              value: c.type_name,
              shippingPrice: c.type_int_shipping_price
            })
          })
        })
    },

    // Process Order modal (single instance pattern, mirrors allOrder.vue)
    openProcessOrderModal(r) {
      const warehouseObj = this.warehouses.find(w => w.value === r.warehouse_id_fk)
      const enriched = Object.assign({}, r, {
        warehouse: {
          transportation_types: warehouseObj ? warehouseObj.transportation_types : []
        }
      })
      this.currentProcessOrder = enriched
      this.checkAlert = false
      this.clearProcessOrderData()
      this.theQuantity.quantity = r.quantity
      this.updateData.quantity_charge = r.quantity_charge
      this.$nextTick(() => {
        if (this.$refs.processComp) {
          this.$refs.processComp.dialog = true
          // Single-instance pattern bypasses processTheOrder.vue's own gear-icon click
          // handler, which is what normally fetches these — without them exchangeBase (etc.)
          // stays 0, so editing a non-USD warehouse order's item price silently fails to
          // recalculate the total (same bug as allOrder.vue, reproduced on order TR-748).
          this.$refs.processComp.calculateTotalAmount()
          this.$refs.processComp.getExchangeRate()
          this.$refs.processComp.getAEDRate()
          this.$refs.processComp.getCNYRate()
          this.$refs.processComp.getGBPRate()
          this.$refs.processComp.getEURRate()
        }
      })
    },

    calculateTotalAmount() {
      let itemPrice = Number(this.updateData.item_price) || 0
      let shipping = Number(this.updateData.internal_shipping_handling) || 0
      let tax = Number(this.updateData.tax) || 0
      let nationalTax = Number(this.updateData.national_tax) || 0
      let delivery = Number(this.updateData.delivery_price) || 0
      let fee = Number(this.updateData.kreenex_fee) || 0
      let insurance = Number(this.updateData.insurance) || 0
      let qttCharge = Number(this.updateData.quantity_charge) || 0
      let discount = Number(this.updateData.discount) || 0
      let qtt = Number(this.theQuantity.quantity) || 0
      let repackagePrice = Number(this.updateData.repackage_price) || 0
      let holdPrice = Number(this.updateData.hold_price) || 0
      let otherServicePrice = Number(this.updateData.other_service_price) || 0
      const _userData = JSON.parse(localStorage.getItem('user_data') || '{}')
      const _exRate = Number(_userData.ex_rate) || 1
      let localDelivery = (Number(this.updateData.local_delivery) || 0) / _exRate

      // Per-unit shipping, matching newOrder.vue/editOrderCharge.vue: item price and shipping
      // are both charged per unit. Confirmed against live data — 76 of 79 existing orders with
      // qty>1 and shipping>0 match (item_price + shipping) * qty, not this flat variant.
      let total = (itemPrice + shipping) * qtt + tax + nationalTax + delivery + fee + insurance + qttCharge + localDelivery

      if (this.updateData.is_repackage) total += repackagePrice
      if (this.updateData.is_hold) total += holdPrice
      if (otherServicePrice > 0) total += otherServicePrice
      if (discount > 0) total -= discount

      this.updateData.total_amount = total.toFixed(2)
    },

    clearProcessOrderData() {
      this.updateData.item_price = null
      this.updateData.the_tl_price = null
      this.updateData.the_aed_price = null
      this.updateData.the_cny_price = null
      this.updateData.the_gbp_price = null
      this.updateData.the_eur_price = null
      this.updateData.tax = null
      this.updateData.item_weight = null
      this.updateData.internal_shipping_handling = 0
      this.updateData.delivery_price = null
      this.updateData.kreenex_fee = null
      this.updateData.insurance = null
      this.updateData.total_amount = null
      this.updateData.from_date = null
      this.updateData.to_date = null
      this.updateData.quantity_charge = null
      this.updateData.description = ''
      this.updateData.item_type = ''
      this.updateData.item_weight_lb = null
      this.updateData.discount = null
      this.updateData.send_sms = false
      this.updateData.is_repackage = false
      this.updateData.is_hold = false
      this.updateData.other_service = ''
      this.updateData.repackage_price = null
      this.updateData.hold_price = null
      this.updateData.other_service_price = null
      this.updateData.local_delivery = null
      this.updateData.tax = null
      this.updateData.national_tax = null
      this.updateData.process_note = ''
      this.updateData.customer_process_note = ''
      this.updateData.customer_id_fk = null
      this.updateData.product_name = null
      this.updateData.product_url = null
      this.updateData.quantity = null
      this.updateData.color = null
      this.updateData.size = null
      this.updateData.note = null
      this.updateData.brand = null
      this.updateData.order_image = null
      this.checkAlert = false
    },

    processOrder(event, id, stime, stime1, stime2) {
      let token = localStorage.getItem('access_token')
      this.loading = true
      this.updateData.send_sms = this.updateData.send_sms.toString()

      const formData = new FormData()

      Object.keys(this.updateData).forEach(key => {
        if (key === 'customer_process_note_image' || key === 'order_image') return
        if (this.updateData[key] !== null && this.updateData[key] !== undefined) {
          formData.append(key, this.updateData[key])
        }
      })

      if (this.updateData.customer_process_note_image instanceof File) {
        formData.append('customer_process_note_image', this.updateData.customer_process_note_image)
      }

      if (this.updateData.order_image instanceof File) {
        formData.append('order_image', this.updateData.order_image)
      }

      axios.post(this.processOrderUrl + '/' + id, formData, {
        headers: { Authorization: 'Bearer ' + token }
      })
        .then(() => {
          this.loading = false
          this.clearProcessOrderData()
          stime.time = ''
          stime1.time = ''
          stime2.time = ''
          this.checkAlert = true
          this.getResults()
          let b = this
          setTimeout(function () {
            b.checkAlert = false
          }, 1500)
        })
        .catch((error) => {
          const rawMsg = error && error.response && error.response.data && error.response.data.message
          const msg = rawMsg && typeof rawMsg === 'object'
            ? Object.values(rawMsg).flat().join('\n')
            : (rawMsg || this.$t('orders.errorOccurredProcessingOrder'))
          swal(this.$t('common.error'), msg, 'error')
          this.loading = false
          this.clearProcessOrderData()
          stime.time = ''
          stime1.time = ''
          stime2.time = ''
        })
    },

    handleWarehouseFilter(val) {
      this.tableData.warehouse_id_fk = Array.isArray(val) && val.length ? val.join(',') : ''
      this.url = 'api/int_shipment_order'
      this.getResults()
    },

    openSendToPendingToBranchModal() {
      this.sendToPendingToBranchModal = true
    },

    closeSendToPendingToBranchModal() {
      this.sendToPendingToBranchModal = false
      this.sendToPendingToBranchData.order_id = ''
      this.$validator.reset()
    },

    sendToPendingToBranch() {
      let token = localStorage.getItem('access_token')

      axios
        .put(
          this.deliverToPendingToBranchUrl + '/' + this.sendToPendingToBranchData.order_id,
          this.sendToPendingToBranchData,
          {
            headers: { Authorization: 'Bearer ' + token }
          }
        )
        .then((response) => {
          this.sendToPendingToBranchData.order_id = ''
          swal({
            title: this.$t('common.success'),
            text: this.$t('orders.sentSuccessfully'),
            icon: 'success',
            timer: 1000
          })
          this.getResults()
        })
        .catch((error) => {
          this.sendToPendingToBranchData.order_id = ''
          swal({
            title: this.$t('common.error'),
            text: error.response.data.message,
            icon: 'error',
            timer: 1000
          })
          this.getResults()
        })
    },

    openSendToBranchDeliveryModal() {
      this.sendToBranchDeliveryModal = true
    },

    closeSendToBranchDeliveryModal() {
      this.sendToBranchDeliveryModal = false
      this.sendToBranchDeliveryData.order_id = ''
      this.$validator.reset()
    },

    // Int Shipment -> Branch Delivery shortcut, single order by QR code: same qrcode-entry
    // pattern as sendToPendingToBranch, but skips straight to Branch Delivery
    // (advance_to_branch_delivery_by_qrcode auto-fills the Pending To Branch date on the backend).
    sendToBranchDelivery() {
      let token = localStorage.getItem('access_token')

      axios
        .put(
          this.advanceToBranchDeliveryByQrcodeUrl + '/' + this.sendToBranchDeliveryData.order_id,
          this.sendToBranchDeliveryData,
          {
            headers: { Authorization: 'Bearer ' + token }
          }
        )
        .then((response) => {
          this.sendToBranchDeliveryData.order_id = ''
          swal({
            title: this.$t('common.success'),
            text: this.$t('orders.sentSuccessfully'),
            icon: 'success',
            timer: 1000
          })
          this.getResults()
        })
        .catch((error) => {
          this.sendToBranchDeliveryData.order_id = ''
          swal({
            title: this.$t('common.error'),
            text: error.response.data.message,
            icon: 'error',
            timer: 1000
          })
          this.getResults()
        })
    },

    deliverToPendingToBranch(id) {
      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('orders.onceDeliveredPendingBranchWarning'),
        icon: 'warning',
        buttons: true
      }).then((willDelete) => {
        if (willDelete) {
          let token = localStorage.getItem('access_token')
          axios
            .put(this.deliverToPendingToBranchUrl + '/' + id, '', {
              headers: { Authorization: 'Bearer ' + token }
            })
            .then(() => {
              this.getResults()
              swal({
                title: this.$t('common.done'),
                text: '',
                icon: 'success',
                timer: 1500,
                buttons: false
              })
            })
            .catch(error => {
              this.getResults()
              swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
            })
        }
      })
    },

    // Int Shipment -> Branch Delivery shortcut: skips the Pending To Branch step entirely.
    // advance_to_branch_delivery auto-fills pending_to_branch_at if the order hasn't passed
    // through that step yet, so this works whether the order is at Int Shipment or already
    // sitting at Pending To Branch.
    sendAllToPendingToBranch() {
      if (this.qrCodeValues.length < 1) {
        swal({
          text: this.$t('orders.pleaseAtLeastSelectOneOrder'),
          icon: 'warning',
          dangerMode: true
        })
        return
      }

      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('orders.confirmSendToBranchDelivery'),
        icon: 'warning',
        buttons: true
      }).then((confirmed) => {
        if (!confirmed) return

        let token = localStorage.getItem('access_token')
        let orderIds = this.qrCodeValues.map((q) => q[0])

        axios
          .put(this.advanceToBranchDeliveryUrl, { orders_id: orderIds }, {
            headers: { Authorization: 'Bearer ' + token }
          })
          .then(() => {
            this.qrCodeValues = []
            this.getResults()
            swal({
              title: this.$t('common.done'),
              text: '',
              icon: 'success',
              timer: 1500,
              buttons: false
            })
          })
          .catch((error) => {
            const msg = (error.response && error.response.data && error.response.data.message)
              ? error.response.data.message
              : this.$t('orders.failedToSendOrdersToBranchDelivery')
            swal(this.$t('common.error'), msg, 'error')
            this.getResults()
          })
      })
    },

    advanceOrderToBranchDelivery(id) {
      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('orders.confirmSendDirectlyToBranchDelivery'),
        icon: 'warning',
        buttons: true
      }).then((willDeliver) => {
        if (willDeliver) {
          let token = localStorage.getItem('access_token')
          axios
            .put(this.advanceToBranchDeliveryUrl, { orders_id: [id] }, {
              headers: { Authorization: 'Bearer ' + token }
            })
            .then(() => {
              this.getResults()
              swal({
                title: this.$t('common.done'),
                text: '',
                icon: 'success',
                timer: 1500,
                buttons: false
              })
            })
            .catch((error) => {
              this.getResults()
              swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
            })
        }
      })
    },

    returnToPurchase(id) {
      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('orders.confirmReturnToPurchasedSection'),
        icon: 'warning',
        buttons: true,
        className: 'danger-swal'
      }).then((willDelete) => {
        if (willDelete) {
          let token = localStorage.getItem('access_token')
          axios
            .put(this.returnToPurchaseUrl + id + '/return_to_purchase', '', {
              headers: { Authorization: 'Bearer ' + token },
            })
            .then((response) => {
              this.getResults()
              swal({
                title: this.$t('common.done'),
                text: '',
                icon: 'success',
                timer: 1500,
                buttons: false,
              })
            })
            .catch((error) => {
              if (error.response) {
                swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
              }
            })
        }
      })
    },

    modal(modal) {
      $('#' + modal).modal({ backdrop: 'static' })
    },

    closeModal(modal) {
      let theModal = $('#' + modal)
      theModal.modal('toggle')
    },

    initDetail(modal, id) {
      let token = localStorage.getItem('access_token')
      axios
        .get(this.orderDetailUrl + '/' + id, { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let data = response.data
          this.detailResults = data.data
          this.detialData = this.detailResults[0]
        })
      this.modal(modal)
    },

    perPageFix(perPage) {
      this.tableData.per_page = perPage
      this.getResults()
    },

    getByWarehouse(warehouse) {
      this.tableData.warehouse = warehouse
      this.getResults()
    },

    changePage(pageUrl) {
      this.url = pageUrl
      this.getResults()
    },

    searchMe(str) {
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      if (str.trim() !== '') {
        this.tableData.search = str
        this.prevSearchQuery = str
        this.getResults()
      } else {
        swal({
          text: this.$t('common.typeToSearch'),
          icon: 'warning',
          dangerMode: true,
        })
        if (this.prevSearchQuery !== '') {
          this.tableData.search = ''
          this.prevSearchQuery = ''
          this.getResults()
        }
      }
    },

    sortByColumn(str) {
      this.tableData.column = str
      this.tableData.sortKey = str
      this.tableData.sortDir = this.tableData.sortDir === 'desc' ? 'asc' : 'desc'
      this.getResults()
    },

    searchOneCol(columnName, str) {
      this.tableData.searchOneCol = columnName
      this.tableData.searchOneVal = str
      this.getResults()
    },

    filterTable(fromDate, toDate) {
      this.tableData.from_date = fromDate
      this.tableData.to_date = toDate
      this.getResults()
    },

    configPagination(data) {
      this.pagination.lastPage = data.last_page
      this.pagination.currentPage = data.current_page
      this.pagination.total = data.total
      this.pagination.lastPageUrl = data.last_page_url
      this.pagination.nextPageUrl = data.next_page_url
      this.pagination.prevPageUrl = data.prev_page_url
      this.pagination.from = data.from
      this.pagination.to = data.to
    },

    checkAll() {
      this.qrCodeValues = this.apiResults.map(r => [r.order_id, r.full_name, r.phone_number, r.user_id, r.theOrderID, r.is_ship_only, r.product_name])
    },

    unCheckAll() {
      this.qrCodeValues = []
    },

    openCancelingOrderModal(orderId, totalAmount) {
      this.cancelOrderModal = true
      this.theCancelOrderID = orderId
      this.cancelRefundAmount = totalAmount || null
    },

    closeCancelingOrderModal() {
      this.cancelOrderModal = false
      this.cancelAlert = false
      this.cancelRefundAmount = null
      this.cancelData.cancel_reason = ''
    },

    cancelingOrder() {
      let token = localStorage.getItem('access_token')
      axios
        .put(this.cancelingOrderUrl + '/' + this.theCancelOrderID, this.cancelData, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then(() => {
          this.getResults()
          this.cancelAlert = true
          setTimeout(() => {
            this.closeCancelingOrderModal()
          }, 1500)
        })
        .catch(error => {
          swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
        })
    },

    getReasons() {
      let token = localStorage.getItem('access_token')
      axios.get('api/reason/list', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let result = response.data
          this.reasons = result.map(r => ({
            text: r.cancel_reason,
            value: r.cancel_reason,
            ar: r.cancel_reason_ar,
            ku: r.cancel_reason_ku
          }))
        })
    },
  },
}
</script>

<style>

.panel-heading h4 {
  text-transform: uppercase;
  margin-left: -20px;
  padding-left: 10px;
  font-weight: 500;
}

.icon-enlarge,
.icon-pencil {
  color: navy;
}

.icon-bin2 {
  color: red;
}

.icon-calendar {
  color: green;
}

.btn {
  transition: all 0.5s;
}

.col-xs-12 {
  padding-top: 10px;
  padding-bottom: 10px;
}
.overlay--active:before {
  opacity: 50 !important;
  background: rgba(0, 0, 0, 0.5) !important;
}

.dialog {
  box-shadow: 0 3px 5px -1px rgba(14, 14, 14, 0.212) !important;
  overflow: hidden;
  overflow-y: hidden;
}
.page-stats {
  align-items: center;
  position: relative;
  top: -8px;
  margin-right: 5px;
}

@media screen and (max-width: 600px) {
  th {
    font-weight: bold;
    color: #273246;
    padding-left: 5px !important;
  }

  td {
    padding-left: 5px !important;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
}

.table > thead > tr > th, .table > tbody > tr > th, .table > tfoot > tr > th, .table > thead > tr > td, .table > tbody > tr > td, .table > tfoot > tr > td
{
  padding: 0 5px !important;
}

.danger-swal {
  background: #ff6262 !important;
}
a:hover {
  color: #03a9f4;
  position: relative;
}
a[tooltip]:hover:after {
  content: attr(tooltip);
  padding: 8px 12px;
  color: #ffffff;
  position: absolute;
  right: 80%;
  bottom: 150%;
  white-space: nowrap;
  z-index: 9999;
  -moz-border-radius: 3px;
  -webkit-border-radius: 3px;
  border-radius: 3px;
  -moz-box-shadow: 0px 0px 2px #484b4e;
  -webkit-box-shadow: 0px 0px 2px #484b4e;
  box-shadow: 0px 0px 2px #484b4e;
  background-color: #484b4e;
}

@media screen and (max-width: 767px) {
  .form-group {
    margin-top: 0;
    margin-bottom: 0;
  }
}

/* Mobile order cards: see assets/mobile-order-cards.css */

.customer-name-stack {
  display: inline-flex;
  flex-direction: column;
  vertical-align: middle;
}
.customer-phone-line {
  display: block;
  font-size: 14px;
  color: #666;
}
.combined-cell-td {
  vertical-align: middle;
}
.combined-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  font-size: 15px;
  line-height: 1.4;
  white-space: nowrap;
}
.combined-cell-label {
  color: #888;
}
</style>



// WEBPACK FOOTER //
// src/components/intShipmentOrder.vue